import React from 'lib/react';
import { EVButton, EVMapOutlinedIcon, makeStyles } from 'lib/components';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import { useState, useEffect } from 'react';
import {
  CRYPTO_KEY, CRYPTO_IV, HOST_URL,
} from 'constants/AppConstants';
import { encryptAndEncode } from 'utils/app.utils';
import { getAuthToken } from 'utils/auth.utils';
import { SITEMAP_APPROVAL_PATH_ROUTE } from 'constants/RouteConstants';
import { useLocation, Link } from 'lib/router';
import { useSelector } from 'lib/redux';
import {
  featureFlagSelector,
  isRedirectExceptionOrgSelector,
} from 'utils/appSelectors.utils';
import * as featureFlags from 'constants/FeatureFlag.constants';

/** New tabs do not share sessionStorage — carry access_token so App can authorize + load entitlements. */
const withAccessTokenQuery = (path) => {
  const accessToken = getAuthToken();
  if (!accessToken) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}access_token=${encodeURIComponent(accessToken)}`;
};

export const useStyles = makeStyles((theme) => ({
  sitemapBtn: {
    width: '125px',
    backgroundColor: theme.evPalette.salt[100],
    borderColor: theme.evPalette.charcoal[300],
    padding: '4px 12px 4px 12px',
  },
  sitemapBtnLabel: {
    columnGap: '8px',
    padding: 0,
    alignItems: 'center',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '19px',
    textTransform: 'none',
  },
}));

function SitemapApprovalButton({ reportId }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [encryptedReportID, setEncryptedReportID] = useState(null);
  const location = useLocation();
  const currentUrl = `${location.pathname}${location.search}`;

  const myevRefreshLegacyRedirectFlag = useSelector(featureFlagSelector(featureFlags.REDIRECT_ACTION_BUTTONS_TO_LEGACY_PORTAL));
  // #genai Hide Order History Sitemap button unless MYEV_SITEMAP_AUTOMATION is enabled.
  const isSitemapAutomationEnabled = useSelector(featureFlagSelector(featureFlags.MYEV_SITEMAP_AUTOMATION));
  const isRedirectExceptionOrg = useSelector(isRedirectExceptionOrgSelector);

  useEffect(() => {
    let isMounted = true;

    encryptAndEncode(reportId, CRYPTO_KEY, CRYPTO_IV)
      .then((encryptedID) => isMounted && setEncryptedReportID(encryptedID));

    return () => { isMounted = false; };
  }, []);

  if (!isSitemapAutomationEnabled) {
    return null;
  }

  const buttonProps = (() => {
    const approvalPath = SITEMAP_APPROVAL_PATH_ROUTE.replace(':encryptedReportId', encryptedReportID);
    // Same-tab Link keeps sessionStorage; new-tab href needs access_token (see getTokenFromUrl).
    const newTabApprovalPath = withAccessTokenQuery(approvalPath);

    // New approval page exists only on MyEV — always open MyEV even when legacy redirect is on.
    if (myevRefreshLegacyRedirectFlag) {
      return ({
        href: HOST_URL + newTabApprovalPath,
        target: '_blank',
      });
    }

    if (isRedirectExceptionOrg) {
      return ({
        component: Link,
        to: approvalPath,
        state: { currentUrl },
      });
    }

    return ({
      href: HOST_URL + newTabApprovalPath,
      target: '_blank',
    });
  })();

  return (
    <EVButton
      id={`sitemapApprovalBtn-${reportId}`}
      variant="outlined"
      color="primary"
      className={classes.sitemapBtn}
      classes={{ label: classes.sitemapBtnLabel }}
      onClick={(e) => e.stopPropagation()}
      disabled={!encryptedReportID}
      {...buttonProps}
    >
      <EVMapOutlinedIcon />
      {t('order_history__actions__sitemap')}
    </EVButton>
  );
}

SitemapApprovalButton.propTypes = {
  reportId: PropTypes.number.isRequired,
};

export default SitemapApprovalButton;
